import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Paper, Typography, Tabs, Tab, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchCarePlanNotReviewedMembersChart, exportCarePlanNotReviewedMembersChart } from '../WidgetService';
import { parentUrl } from '../../../utils/constants';
import { dateFormatter } from '../../../utils/timeFormat';
import TeamDropdown from '../../Shared/TeamDropdown';
import { fetchTeams } from '../../../store/careTeams.slice';
import { MissingCarePlanSignatureTypeEnum } from '../../../Enum/MIssingCarePlanSignatureTypeEnum';
import MemberPreview from '../../../Models/Member/MemberPreview.model';
import { isDefaultDate } from '../../../utils/common';
import { CarePlanNotReviewedPeriodEnum } from '../../../Enum/CarePlanNotReviewedPeriodEnum';
import '../Widgets.css';

export const carePlanNotReviewedDateList = [
    {
        Id: CarePlanNotReviewedPeriodEnum.Last90Days,
        Value: CarePlanNotReviewedPeriodEnum.Last90DaysDescription,
    },
    {
        Id: CarePlanNotReviewedPeriodEnum.Last180Days,
        Value: CarePlanNotReviewedPeriodEnum.Last180DaysDescription,
    },
    {
        Id: CarePlanNotReviewedPeriodEnum.Last1Year,
        Value: CarePlanNotReviewedPeriodEnum.Last1YearDescription,
    },
    {
        Id: CarePlanNotReviewedPeriodEnum.Custom,
        Value: CarePlanNotReviewedPeriodEnum.CustomDescription,
    },
];

const CarePlanNotReviewedSummaryDetail = () => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const {
        carePlanSelectedTeam,
        setCarePlanSelectedTeam,
        carePlanDateFilter,
        setCarePlanDateFilter,
        carePlanDate,
        setCarePlanDate,
        carePlanShowInActiveTeams,
        setCarePlanShowInActiveTeams,
    } = useWidgetContext();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [tab, setTab] = useState(1);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const [members, setMembers] = useState<MemberPreview[]>([]);
    const [filteredRows, setFilteredRows] = useState<MemberPreview[]>([]);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let dateList = carePlanNotReviewedDateList;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        setIsFetched(true);
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (carePlanDateFilter !== CarePlanNotReviewedPeriodEnum.Custom || Boolean(carePlanDate)) {
            setMembers([]);
            setIsFetched(false);
            const changeFilter = async () => {
                setIsFetched(false);
                setIsMembersLoading(true);
                const membersInfo = await fetchCarePlanNotReviewedMembersChart(
                    carePlanSelectedTeam,
                    carePlanDateFilter,
                    carePlanDate ? new Date(carePlanDate).toISOString() : '',
                    carePlanShowInActiveTeams
                );
                setMembers(membersInfo);

                let filterResponse = [];
                switch (tab) {
                    case 1: {
                        filterResponse = membersInfo.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.NotReviewed);
                        break;
                    }
                    case 2: {
                        filterResponse = membersInfo.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Member);
                        break;
                    }
                    case 3: {
                        filterResponse = membersInfo.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Provider);
                        break;
                    }
                }
                setFilteredRows(filterResponse);
                setIsFetched(true);
                setIsMembersLoading(false);
            };
            changeFilter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carePlanSelectedTeam, carePlanShowInActiveTeams, carePlanDate, carePlanDateFilter]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const exportCarePlanNotReviewedMembers = async () => {
        setDownLoading(true);
        const response = await exportCarePlanNotReviewedMembersChart(
            carePlanSelectedTeam,
            carePlanDateFilter,
            carePlanDate ? new Date(carePlanDate).toISOString() : '',
            carePlanShowInActiveTeams
        );
        let blob = b64toBlob(response.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `CarePlanNotReviewedMembers - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const columns: GridColDef[] = [
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 230,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 230,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 230,
        },
        {
            field: 'LastSignedDate',
            headerName: 'Last Date Signed/Updated',
            width: 250,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastSignedDate) ? '-' : dateFormatter(new Date(params.row.LastSignedDate))}</>;
            },
        },
        {
            field: 'UpcomingAppointmentDate',
            headerName: 'Next Upcoming Appointment Date',
            width: 250,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.UpcomingAppointmentDate) ? '-' : dateFormatter(new Date(params.row.UpcomingAppointmentDate))}</>;
            },
        },
        {
            field: 'LastCompletedAppointmentDate',
            headerName: 'Last Completed Appointment Date',
            width: 250,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastCompletedAppointmentDate) ? '-' : dateFormatter(new Date(params.row.LastCompletedAppointmentDate))}</>;
            },
        },
    ];

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        let filterResponse = [];
        switch (newValue) {
            case 1: {
                filterResponse = members.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.NotReviewed);
                break;
            }
            case 2: {
                filterResponse = members.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Member);
                break;
            }
            case 3: {
                filterResponse = members.filter((m) => m.MissingCarePlanSignatureType === MissingCarePlanSignatureTypeEnum.Provider);
                break;
            }
        }
        setFilteredRows(filterResponse);
        setTab(newValue);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Members Missing Care Plan Signature
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={2}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={carePlanSelectedTeam}
                            onChange={(id) => setCarePlanSelectedTeam(id)}
                            showInactive={carePlanShowInActiveTeams}
                            onToggleInactive={setCarePlanShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={carePlanDateFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setCarePlanDateFilter(parseInt(e.target.value as any))}
                            >
                                {dateList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {carePlanDateFilter === CarePlanNotReviewedPeriodEnum.Custom ? (
                        <Grid size={1.5}>
                            <DesktopDatePicker
                                label="Date"
                                value={Boolean(carePlanDate) ? carePlanDate : null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => setCarePlanDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </Grid>
                    ) : (
                        <Grid size={1.5}></Grid>
                    )}
                    <Grid size={5.7}></Grid>
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportCarePlanNotReviewedMembers()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || members?.length === 0}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2} paddingTop={2}>
                    <Grid size={12} display={'flex'} mb={2.5}>
                        <Tabs sx={{ display: 'inline-flex', marginRight: 'auto' }} value={tab} onChange={handleAlertsTabChange}>
                            <Tab value={1} label="Not Reviewed" />
                            <Tab value={2} label="Missing Member Signature" />
                            <Tab value={3} label="Missing Provider Signature" />
                        </Tabs>
                    </Grid>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <div style={{ width: '100%', height: 'calc(100vh - 100px)', maxHeight: '500px', position: 'relative' }}>
                                        <Grid size={12}>
                                            <div style={{ width: '100%', height: 'calc(100vh - 100px)', maxHeight: '500px' }}>
                                                <DataGrid
                                                    onCellClick={(params: any) => {
                                                        window.open(
                                                            `${parentUrl}provider/members-list/member-chart/${params.row.Id}/integrated-care-plan`,
                                                            '_blank'
                                                        );
                                                    }}
                                                    rows={filteredRows}
                                                    columns={columns}
                                                    paginationModel={{ pageSize: pageSize, page: page }}
                                                    onPaginationModelChange={(model) => {
                                                        setPageSize(model.pageSize);
                                                        setPage(model.page);
                                                    }}
                                                    pageSizeOptions={[5]}
                                                    getRowId={(row) => row.Id}
                                                    getRowHeight={() => 'auto'}
                                                    sx={{
                                                        [`& .${gridClasses.cell}`]: {
                                                            py: 1,
                                                        },
                                                        '& .is-late': {
                                                            color: 'red',
                                                        },
                                                        '& .is-in-progress': {
                                                            color: 'green',
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                    </div>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default CarePlanNotReviewedSummaryDetail;
